import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Edit, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import MarkdownEditor from '../components/MarkdownEditor';
import * as LucideIcons from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminTools = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const response = await axios.get(`${API_URL}/tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTools(response.data);
    } catch (error) {
      console.error('Erreur chargement outils:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (toolData) => {
    try {
      await axios.put(
        `${API_URL}/admin/tools/${editingTool.id}`,
        toolData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Outil mis à jour');
      fetchTools();
      setIsDialogOpen(false);
      setEditingTool(null);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 bg-sand-50">
      {/* Header */}
      <div className="bg-sage-500 px-6 py-8 text-white">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/contents')}
          className="text-white hover:bg-sage-600 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-4xl font-serif font-light mb-2">Outils</h1>
        <p className="text-white/80 font-sans">Gérer les 8 outils pratiques</p>
      </div>

      {/* Tools grid */}
      <div className="px-6 py-6 grid grid-cols-1 gap-4">
        {tools.map((tool) => {
          const IconComponent = LucideIcons[tool.icon] || Sparkles;
          return (
            <div
              key={tool.id}
              className="bg-white rounded-xl border border-sand-200 p-5"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="w-12 h-12 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-6 h-6 text-sage-500" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs bg-sage-100 text-sage-700 px-2 py-0.5 rounded font-mono">
                        {tool.slug}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg text-sage-700 mb-2">{tool.title}</h3>
                    <p className="text-sm text-sage-700/70 font-sans mb-2">{tool.description}</p>
                    <p className="text-xs text-sage-700/60 font-sans">
                      {tool.content.substring(0, 100)}...
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditingTool(tool);
                    setIsDialogOpen(true);
                  }}
                  className="text-sage-500 hover:text-sage-700 ml-4"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        {editingTool && (
          <ToolDialog
            tool={editingTool}
            onSave={handleSave}
            onClose={() => setIsDialogOpen(false)}
          />
        )}
      </Dialog>
    </div>
  );
};

const ToolDialog = ({ tool, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: tool?.title ?? '',
    description: tool?.description ?? '',
    content: tool?.content ?? '',
    icon: tool?.icon ?? 'Sparkles'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const iconOptions = [
    'Clock', 'Eye', 'BookMarked', 'UtensilsCrossed', 'Pizza', 
    'Wine', 'Heart', 'CalendarCheck', 'Sparkles'
  ];

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl font-serif text-sage-700">
          Modifier l'outil
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label>Titre</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>

        <div>
          <Label>Description courte</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
          />
        </div>

        <div>
          <Label>Icône (Lucide Icon)</Label>
          <select
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            className="w-full px-3 py-2 border border-sand-200 rounded-lg"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>

        <div>
          <Label>Contenu (Markdown)</Label>
          <MarkdownEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
            placeholder="# Titre&#10;&#10;Votre contenu en Markdown..."
            minHeight="400px"
          />
        </div>

        <div className="flex space-x-3">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
          <Button type="submit" className="flex-1 bg-sage-500 hover:bg-sage-600 text-white">
            Enregistrer
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default AdminTools;
